import { crud } from '@think-internet/zeus-frontend-package'
import { calculateAnwesenheitPercentage, calculateResultsOfUnit, getSumOfConcept } from '../../../../hooks/useAnalysisCalculation/calculateResults'
import { Survey } from '../../../../../types/Survey/Survey'
import { Answer } from '../../../../../types/Survey/Answer/Answer'
import { Analysis } from '../../../../../types/Survey/Analysis/Analysis'
import props from '../../../../../redux/props'
import { Concept, ConceptDataCluster, ConceptUnitList, ConceptValues } from '../../../../../types/Survey/Analysis/Concept'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { setLocal } from '../../../../../redux/action/local'
import {
  getEinzelbueros,
  getSonderbedarfe,
  getStaufleache,
  getWorkstations,
} from '../../../../hooks/useAnalysisCalculation/byBueroType/helper/getAnswerValues'
import { PrebuildType } from '../../../../../types/Survey/Element'
import getLocker from '../../../../hooks/useAnalysisCalculation/byBueroType/helper/getLocker'
import getWorkcafe from '../../../../hooks/useAnalysisCalculation/byBueroType/helper/getWorkcafe'
import getGarderobe from '../../../../hooks/useAnalysisCalculation/byBueroType/helper/getGarderobe'
import getDrucker from '../../../../hooks/useAnalysisCalculation/byBueroType/helper/getDrucker'

type Run = (concept: Concept) => Promise<Concept>

type Init = (teamSurvey: Survey, managerSurvey: Survey, leaderSurvey: Survey) => Promise<void>

type Response = {
  run: Run
  init: Init
}

const useCompleteConceptUnits = (): Response => {
  const dispatch = useDispatch()
  const analysisFeature = crud.use(props.SURVEY_ANALYSIS)
  const answerFeature = crud.use(props.SURVEY_ANSWER)
  const cluster: ConceptDataCluster = useSelector((s) => s[props.SURVEY_ANALYSIS_CONCEPT_DATA_CLUSTER])

  // get the organization unit analysis
  const getOrganizationUnitAnalysis = async (unitUUID: string) => {
    const analysis = cluster.allTeamAnalysis.find((a) => a.organizationUnitUUID === unitUUID)
    return analysis
  }

  // get the organization unit manager answer
  const getOrganizationUnitManagerAnswer = async (unitUUID: string) => {
    const answer = cluster.allManagerAnswers.find((a) => a.organizationUnitUUID === unitUUID)
    return answer
  }

  const pullGlobalAnalysis = async (teamSurvey: Survey) => {
    const query = {
      surveyUUID: teamSurvey.uuid,
      dayNumber: { $exists: false },
      organizationUnitUUID: { $exists: false },
    }
    const analysisList: Analysis = await analysisFeature.get(query)
    return analysisList
  }

  const pullAllTeamAnalysis = async (teamSurvey: Survey) => {
    const query = {
      surveyUUID: teamSurvey.uuid,
      dayNumber: { $exists: false },
      organizationUnitUUID: { $exists: true },
    }
    const analysisList: Analysis[] = await analysisFeature.list(query)
    return analysisList
  }

  const pullAllManagementAnswers = async (managerSurvey: Survey) => {
    const query = {
      surveyUUID: managerSurvey.uuid,
      dayNumber: { $exists: false },
      organizationUnitUUID: { $exists: true },
    }
    const answerList: Answer[] = await answerFeature.list(query)
    return answerList
  }

  const getLeaderAnswer = async (leaderSurvey: Survey) => {
    const query = {
      surveyUUID: leaderSurvey.uuid,
      dayNumber: { $exists: false },
    }
    const answer: Answer = await answerFeature.get(query)
    return answer
  }

  const init: Init = async (teamSurvey, managerSurvey, leaderSurvey) => {
    if (!cluster) {
      const globalAnalysis = await pullGlobalAnalysis(teamSurvey)
      const allTeamAnalysis = await pullAllTeamAnalysis(teamSurvey)
      const allManagerAnswers = await pullAllManagementAnswers(managerSurvey)
      const leaderAnswer = await getLeaderAnswer(leaderSurvey)
      const cluster: ConceptDataCluster = {
        teamSurvey,
        managerSurvey,
        leaderSurvey,
        globalAnalysis,
        allTeamAnalysis,
        allManagerAnswers,
        leaderAnswer,
      }
      dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_DATA_CLUSTER, cluster))
    }
  }

  const run: Run = async (concept) => {
    if (!!cluster) {
      const conceptCopy = _.cloneDeep(concept)

      const newUnitList: ConceptUnitList = {}

      if (!!conceptCopy.units) {
        const unitUUIDList = Object.keys(conceptCopy.units).filter((u) => u !== 'sum')
        for (const unitUUID of unitUUIDList) {
          const unit: ConceptValues = _.cloneDeep(conceptCopy.units[unitUUID])

          const analysis: Analysis = await getOrganizationUnitAnalysis(unit.unitUUID)
          const managerAnswer: Answer = await getOrganizationUnitManagerAnswer(unit.unitUUID)

          unit.teamSurvey = cluster.teamSurvey
          unit.managerSurvey = cluster.managerSurvey
          unit.leaderSurvey = cluster.leaderSurvey
          unit.analysis = analysis
          unit.managerAnswer = managerAnswer
          unit.leaderAnswer = cluster.leaderAnswer
          unit[PrebuildType.EINZELBUEROS] = getEinzelbueros(unit)
          unit[PrebuildType.STAUFLAECHE] = getStaufleache(unit)
          unit[PrebuildType.SONDERBEDARFE] = getSonderbedarfe(unit)
          unit[PrebuildType.WORKSTATIONS] = getWorkstations(unit)
          unit.drucker = getDrucker(unit)
          unit.locker = getLocker(unit)
          unit.workcafe = getWorkcafe(unit)
          unit.garderobe = getGarderobe(unit)

          unit.ergebnisse = calculateResultsOfUnit(unit.bueroType, unit, conceptCopy)

          if (!unit.anwesenheitPercentage || !unit.anwesenheitPercentage.answerAmount) {
            const anwesenheit = calculateAnwesenheitPercentage(cluster.teamSurvey, analysis, unit.bueroType, unit)
            if (isFinite(unit.anwesenheitPercentage.percentage)) {
              unit.anwesenheitPercentage = {
                percentage: unit.anwesenheitPercentage.percentage,
                originalPercentage: unit.anwesenheitPercentage.originalPercentage,
                answerAmount: anwesenheit.answerAmount,
              }
            } else {
              unit.anwesenheitPercentage = anwesenheit
            }
          }

          newUnitList[unitUUID] = unit
        }
      }

      conceptCopy.units = newUnitList
      conceptCopy.units.sum = getSumOfConcept(cluster.teamSurvey.context, conceptCopy, conceptCopy.units)

      return conceptCopy
    }
  }

  return { run, init }
}

export default useCompleteConceptUnits
